import React, { useCallback, useMemo, useState } from 'react';
import { YStack, XStack, styled, Button } from 'tamagui';
import { Virtuoso } from 'react-virtuoso';

import { CobuStack, CobuText } from '@cobu/ui';
import AutoLinkText from '../chat/groupDiscussion/AutoLinkText';
import { GroupGatheringCard } from './GroupGatheringCard';
// import { GroupMembers } from '@/components/GroupMembers';
import AllMembersModal from '../ui/AllMembersModal';

import { Group } from '@cobu/api-client';
import { GroupGathering, GroupMember } from '../../store/groupDetailSlice';
import { useGroupInfo } from '../../hooks/groups/useGroupInfo';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import GroupMembers from '../GroupMembers/GroupMembers';
import { useChannelList } from '../../hooks/useChannelList';

interface GroupInfoDetailsProps {
    group: Group;
    gatherings: GroupGathering[];
    members: GroupMember[];
    userIsGroupMember: boolean;
}

// Styled components matching legacy design exactly
const ContentContainer = styled(YStack, {
    p: '$4', // 16px padding to match legacy contentContainer
});

const SectionContainer = styled(YStack, {
    my: '$4', // 16px marginVertical to match legacy sectionContainer
});

const SectionTitle = (props: React.ComponentProps<typeof CobuText>) => (
    <CobuText
        color="$cobuDarkBlue"
        variant="subheading"
        fow="$semibold"
        {...props}
    />
);

const ShowMoreButton = styled(Button, {
    mt: '$2',
    bg: 'transparent',
    borderWidth: 0,
    pressStyle: {
        backgroundColor: 'transparent',
    },
    hoverStyle: {
        backgroundColor: 'transparent',
    },
    focusStyle: {
        backgroundColor: 'transparent',
    },
});

const ShowMoreText = styled(CobuText, {
    color: '$cobuTeal',
    fontSize: '$3',
    fontWeight: 'bold',
});

const MembersContainer = styled(YStack, {
    mb: '$4', // 16px to match legacy bottomGutter
});

export const GroupInfoDetails: React.FC<GroupInfoDetailsProps> = React.memo(({
    group,
    gatherings,
    members,
    userIsGroupMember,
}) => {
    const {
        pastGatherings,
        shouldShowMembers,
        truncatedDescription,
        showFullDescription,
        strings,
        currentUserId,
        handleShowMore,
        handleMemberPress,
        handleGatheringPress,
    } = useGroupInfo({
        group,
        gatherings,
        members,
        userIsGroupMember,
    });

    const { showEvents } = useFeatureFlags();
    const [showMembersModal, setShowMembersModal] = useState(false);
    const {
        navigateToChannel,
    } = useChannelList();

    const handleShowAllMembers = useCallback(() => {
        setShowMembersModal(true);
    }, []);

    const closeMembersModal = useCallback(() => setShowMembersModal(false), []);

    const residents = members.filter(m => m.role.value === 0 || m.role.name === 'RESIDENT');

    const filteredResidents = useMemo(() => {
        const isBlocked = (m: GroupMember) => m.user.blocked;
        const isDeactivated = (m: GroupMember) => m.user.deactivated;
        return members.filter(m => !isBlocked(m) && !isDeactivated(m));
    }, [members]);

    const allMembersRsvps = useMemo(() => {
        return filteredResidents.map((member) => ({
            uuid: member.uuid,
            totalAttendees: 1,
            user: {
                uuid: member.user.uuid,
                name: `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim(),
                displayName: member.user.displayName || '',
                profileImage: member.user.profileImage,
                role: member.role?.name,
            },
            createdAt: new Date().toISOString(),
        }));
    }, [filteredResidents]);

    const eventTitle = group?.name || strings.groups.info.members;

    return (
        <ContentContainer>
            {/* About Section */}
            <SectionContainer>
                <SectionTitle>{strings.groups.info.about}</SectionTitle>
                <CobuStack>
                    <AutoLinkText
                        text={truncatedDescription}
                        variant="body"
                        color="$cobuDarkBlue"
                        lineHeight={24}
                    />
                    {group?.description && group?.description?.length > 200 && (
                        <ShowMoreButton onPress={handleShowMore}>
                            <ShowMoreText>
                                {showFullDescription ? strings.groups.info.showLess : strings.groups.info.showMore}
                            </ShowMoreText>
                        </ShowMoreButton>
                    )}
                    <CobuText variant="body" color="$colorSecondary" mt="$2">
                        {strings.groups.info.openToAllResidents}
                    </CobuText>
                </CobuStack>
            </SectionContainer>

            {/* Members Section */}
            {shouldShowMembers && (
                <SectionContainer>
                    <SectionTitle>{strings.groups.info.members}</SectionTitle>
                    <MembersContainer>
                        <GroupMembers
                            maxDisplay={8}
                            onMemberPress={(member) => {
                                const originalMember = residents.find(m => m.uuid === member.uuid);
                                if (originalMember) {
                                    handleMemberPress(originalMember);
                                }
                            }}
                            onShowAllMembers={handleShowAllMembers}
                            members={residents}
                        />
                    </MembersContainer>
                </SectionContainer>
            )}

            {/* Past Gatherings Section */}
            {showEvents && userIsGroupMember && pastGatherings.length > 0 && (
                <SectionContainer>
                    <SectionTitle>{strings.groups.info.pastGatherings}</SectionTitle>
                    <Virtuoso
                        data={pastGatherings}
                        computeItemKey={(index, item) => `${item.uuid}-${item.scheduledStartTime}`}
                        itemContent={(index, item) => (
                            <YStack style={{ marginBottom: 12 }}>
                                <GroupGatheringCard
                                    gathering={item}
                                    onPress={() => handleGatheringPress(item)}
                                    showPastIndicator={true}
                                />
                            </YStack>
                        )}
                        useWindowScroll
                        overscan={400}
                        style={{ width: '100%' , height: 220}}
                    />
                </SectionContainer>
            )}

            {/* Empty state if no content */}
            {!group?.description && !shouldShowMembers && pastGatherings.length === 0 && (
                <SectionContainer>
                    <CobuText
                        color="$colorSecondary"
                        variant="body"
                        ta="center"
                        py="$8"
                    >
                        {strings.groups.info.noAdditionalInfo}
                    </CobuText>
                </SectionContainer>
            )}

            {/*All Members Modal */}
            <AllMembersModal
                visible={showMembersModal}
                onClose={closeMembersModal}
                rsvps={allMembersRsvps}
                eventTitle={eventTitle}
                currentUserId={currentUserId ?? undefined}
                totalAttendance={allMembersRsvps.length}
                dmFeatureFlag={true}
            />
        </ContentContainer>
    );
});
